import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Auth from "../pages/Auth";
import UserHome from "../pages/User/Home";
import Dashboard from "../pages/GSO/Dashboard";
import LostItems from "../pages/GSO/LostItems";
import FoundItems from "../pages/GSO/FoundItems";
import PendingClaims from "../pages/GSO/PendingClaims";
import Messages from "../pages/User/Messages";
import ReportFoundItem from "../pages/User/ReportFoundItem";
import ClaimsReported from "../pages/User/ClaimsReported";
import AboutUs from "../pages/User/Guide";
import Guide from "../pages/User/Guide";
import GSOMessages from "../pages/GSO/Messages";
import NotificationOverlay from "../components/GSO/NotificationOverlay";
import TotalItemsReported from "../pages/GSO/TotalItemsReported";
import TotalItemsReturned from "../pages/GSO/TotalItemsReturned";
import MostCommonLostItems from "../pages/GSO/MostCommonLostItems";
import PeakTimeLostItems from "../pages/GSO/PeakTimeLostItems";
import Heatmap from "../pages/GSO/Heatmap";
import Processing from "../pages/User/Processing";
import Result from "../pages/User/Result";
import { SidebarProvider } from "../components/SidebarContext";
import AdminDashboard from "../pages/Admin/AdminDashboard";
import Profile from "../pages/User/Profile";
import Settings from "../pages/User/Settings";
import Support from "../pages/User/Support";
import FAQ from "../pages/User/FAQ";
import ProtectedRoute, { DEV_MODE } from "../components/ProtectedRoute";

// Function to check session on page load
const SessionHandler = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // DEV MODE: Skip to user home directly
    if (DEV_MODE) {
      navigate("/user/home", { replace: true });
      setLoading(false);
      return;
    }

    const authToken = localStorage.getItem("authToken");
    const userRole = localStorage.getItem("userRole");

    if (!authToken) {
      navigate("/auth", { replace: false });
    } else {
      if (userRole === "GSO" || userRole === "gso") {
        navigate("/gso/dashboard", { replace: true });
      } else if (userRole === "ADMIN" || userRole === "CO-ADMIN") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/user/home", { replace: true });
      }
    }

    setLoading(false);
  }, [navigate]);

  return loading ? null : null;
};

const RoutesComponent = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Protected Admin Route */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["ADMIN", "CO-ADMIN"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<SessionHandler />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        <Route path="/signup" element={<Navigate to="/auth" replace />} />
        
        {/* Protected User Routes with SidebarProvider */}
        <Route
          path="/user/*"
          element={
            <ProtectedRoute allowedRoles={["USER"]}>
              <SidebarProvider>
                <Routes>
                  <Route path="home" element={<UserHome />} />
                  <Route path="report-found-item" element={<ReportFoundItem />} />
                  <Route path="search-history" element={<ClaimsReported />} />
                  <Route path="guide" element={<Guide />} />
                  <Route path="notification" element={<Messages />} />
                  <Route path="about-us" element={<AboutUs />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="support" element={<Support />} />
                  <Route path="faqs" element={<FAQ />} />
                </Routes>
              </SidebarProvider>
            </ProtectedRoute>
          }
        />
        <Route path="/home" element={<ProtectedRoute allowedRoles={["USER"]}><SidebarProvider><UserHome /></SidebarProvider></ProtectedRoute>} />
        <Route path="/processing" element={<ProtectedRoute allowedRoles={["USER"]}><Processing /></ProtectedRoute>} />
        <Route path="/result" element={<ProtectedRoute allowedRoles={["USER"]}><Result /></ProtectedRoute>} />

        {/* Protected GSO Routes */}
        <Route
          path="/gso/*"
          element={
            <ProtectedRoute allowedRoles={["GSO"]}>
              <SidebarProvider>
                <Routes>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="lost-items" element={<LostItems />} />
                  <Route path="found-items" element={<FoundItems />} />
                  <Route path="pending-claims" element={<PendingClaims />} />
                  <Route path="notification" element={<GSOMessages />} />
                  <Route path="notification-overlay" element={<NotificationOverlay />} />
                  <Route path="total-items-reported" element={<TotalItemsReported />} />
                  <Route path="total-items-returned" element={<TotalItemsReturned />} />
                  <Route path="most-common-lost-items" element={<MostCommonLostItems />} />
                  <Route path="peak-time-lost-items" element={<PeakTimeLostItems />} />
                  <Route path="heatmap" element={<Heatmap />} />
                </Routes>
              </SidebarProvider>
            </ProtectedRoute>
          }
        />

        {/* Default Redirect for Any Unknown Route */}
        <Route path="*" element={<Navigate to="/auth" replace />} />
      </Routes>
    </Router>
  );
};

export default RoutesComponent;
